"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useGameStore } from "@/stores/game-store";

/**
 * BattleScene — props-based layered pixel-art battle backdrop.
 *
 * Replaces the old flat 4-color gradient div with a real, biome-aware scene:
 * a palette-conform CSS sky gradient (back), a repeat-x distant-scenery strip
 * (mid), a repeat-x near-ground band (bottom), and two elliptical platform pads
 * — one under the opponent fighter (upper-right), one under the player fighter
 * (lower-left) — so scale-3 BattleSprites read as standing on the ground rather
 * than floating.
 *
 * All layers use image-rendering: pixelated. PNG assets are referenced by URL
 * (no manifest/sprite-cache); a missing PNG degrades silently to the gradient
 * because every layer is a CSS background-image, which renders nothing — never a
 * broken-image icon — when the file is absent.
 *
 * A one-shot ~450ms entry animation (cream flash + scene rise) plays on mount.
 * A complementary ~400ms exit (iris-close + dusk fade) plays once a side faints
 * — the visible end-of-battle beat — so the scene doesn't pop out. Palette per
 * PIXEL_STYLE.md — no pure black/white, no neon.
 */

const PIXELATED = { imageRendering: "pixelated" as const };

const BIOME_KEYS = ["grass", "cave", "office", "creative", "ops"] as const;
type BiomeKey = (typeof BIOME_KEYS)[number];

function normalizeBiome(key: string): BiomeKey {
  return (BIOME_KEYS as readonly string[]).includes(key)
    ? (key as BiomeKey)
    : "grass";
}

// Biome-tinted sky gradients — palette-conform (PIXEL_STYLE master palette).
// Discrete-ish steps, not photographic blends.
const SKY_GRADIENTS: Record<BiomeKey, string> = {
  // warm hazy sky → pale horizon (water/sky ramp + foliage highlight)
  grass:
    "linear-gradient(to bottom, #a8d2e8 0%, #cfe7f5 45%, #bcd4a0 70%, #7a9a4a 100%)",
  // deep ocean-blue → ink darkness (water ramp shadow + ink)
  cave:
    "linear-gradient(to bottom, #3d6890 0%, #2a3a4a 55%, #1f2330 80%, #1a1a1a 100%)",
  // cool grey-blue office light (shallow water + bronze floor)
  office:
    "linear-gradient(to bottom, #a8d2e8 0%, #b8c4cc 50%, #8a7a5c 78%, #5c4a30 100%)",
  // soft mystic-purple → cream (accent mystic + earth highlight)
  creative:
    "linear-gradient(to bottom, #a04ab8 0%, #b78ac4 40%, #cfb8d4 70%, #f5ecd6 100%)",
  // dusty bronze / amber ops (gold accent + earth ramp)
  ops:
    "linear-gradient(to bottom, #c5a83a 0%, #b89968 45%, #8a7a5c 75%, #5c4a30 100%)",
};

const asset = (biome: BiomeKey, part: "scenery" | "ground" | "platform") =>
  `url("/assets/surggies/battle/${biome}_${part}.png")`;

// ── Cinematic atmosphere pass (Agent B) ────────────────────────────────────
// CSS/DOM-only HD-2D-style post layered OVER the pixel scene: a per-biome
// color-grade, drifting ambient particles, a soft radial light behind the
// fighters, a stronger vignette, and (grass/creative) a god-ray streak.
//
// PERF: every layer is a static absolutely-positioned div. Particle motion is
// pure CSS @keyframes on `transform`/`opacity` (compositor-only, no JS per
// frame, no per-frame allocation). Particle field is computed ONCE per biome
// via useMemo. Toggle the whole pass with POSTFX; per-effect intensities live
// in the config below. Palette-coherent (PIXEL_STYLE master palette), subtle.
const POSTFX = true;

interface AtmoConfig {
  /** Soft-light/multiply grade tint laid over the whole backdrop. */
  grade: { color: string; blend: "soft-light" | "multiply"; opacity: number };
  /** Radial light glow behind the fighters (additive feel via screen blend). */
  glow: { color: string; opacity: number };
  /** Edge vignette strength (0..1) — darkens corners for depth. */
  vignette: number;
  /** Ambient particle field. */
  particles: { count: number; color: string; size: number; kind: "rise" | "drift" };
  /** Optional diagonal god-ray streak (grass/creative). */
  godray: string | null;
}

const ATMO: Record<BiomeKey, AtmoConfig> = {
  // Warm meadow: gold light, pollen rising, soft green-gold grade.
  grass: {
    grade: { color: "#c5a83a", blend: "soft-light", opacity: 0.22 },
    glow: { color: "#f5ecd6", opacity: 0.28 },
    vignette: 0.42,
    particles: { count: 16, color: "#cfe7f5", size: 3, kind: "rise" },
    godray: "rgba(245,236,214,0.16)",
  },
  // Deep cavern: cool ocean-ink grade, slow dust drifting, deep glow.
  cave: {
    grade: { color: "#3d6890", blend: "multiply", opacity: 0.3 },
    glow: { color: "#6a9bc4", opacity: 0.22 },
    vignette: 0.6,
    particles: { count: 14, color: "#b89968", size: 2, kind: "drift" },
    godray: null,
  },
  // Cool interior: grey-blue grade, slow floating dust.
  office: {
    grade: { color: "#a8d2e8", blend: "soft-light", opacity: 0.2 },
    glow: { color: "#cfe7f5", opacity: 0.2 },
    vignette: 0.46,
    particles: { count: 12, color: "#f5ecd6", size: 2, kind: "drift" },
    godray: null,
  },
  // Mystic studio: purple grade, sparkles, soft purple light + god-ray.
  creative: {
    grade: { color: "#a04ab8", blend: "soft-light", opacity: 0.24 },
    glow: { color: "#cfb8d4", opacity: 0.3 },
    vignette: 0.48,
    particles: { count: 18, color: "#f5ecd6", size: 3, kind: "rise" },
    godray: "rgba(207,184,212,0.18)",
  },
  // Amber ops: warm gold grade, embers rising, warm glow.
  ops: {
    grade: { color: "#c5a83a", blend: "multiply", opacity: 0.24 },
    glow: { color: "#c5a83a", opacity: 0.26 },
    vignette: 0.5,
    particles: { count: 16, color: "#c5a83a", size: 3, kind: "rise" },
    godray: null,
  },
};

interface Mote {
  left: number; // %
  top: number; // %
  size: number; // px
  delay: number; // s
  dur: number; // s
  drift: number; // px horizontal sway amplitude
  opacity: number;
}

// Deterministic field so SSR/CSR markup matches (no Math.random in render).
// Computed once per biome via useMemo; pure data, zero per-frame work.
function buildMotes(biome: BiomeKey): Mote[] {
  const cfg = ATMO[biome].particles;
  const out: Mote[] = [];
  // Cheap LCG seeded from biome name — stable across renders.
  let seed = 0;
  for (let i = 0; i < biome.length; i++) seed = (seed * 31 + biome.charCodeAt(i)) >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  for (let i = 0; i < cfg.count; i++) {
    out.push({
      left: rnd() * 100,
      top: rnd() * 100,
      size: cfg.size * (0.6 + rnd() * 0.9),
      delay: -rnd() * 14, // negative → staggered, already mid-animation on mount
      dur: 9 + rnd() * 9,
      drift: (rnd() * 2 - 1) * 14,
      opacity: 0.3 + rnd() * 0.45,
    });
  }
  return out;
}

export interface BattleSceneProps {
  backdropKey: string;
  /** Force the one-shot exit (iris + fade). When omitted, the scene self-arms
   *  it the moment either side's store HP reaches 0. */
  exiting?: boolean;
}

export function BattleScene({ backdropKey, exiting }: BattleSceneProps) {
  const biome = normalizeBiome(backdropKey);
  const atmo = ATMO[biome];
  // Particle field — built once per biome, never re-allocated per frame.
  const motes = useMemo(() => buildMotes(biome), [biome]);

  // Self-arm the exit on the first faint of the battle (HP → 0). This is the
  // visible end-of-battle beat and happens while the arena is still mounted,
  // unlike the active→false flip which tears the whole tree down at once.
  const opponentHp = useGameStore((s) => s.battle.opponentHpCurrent);
  const playerHp = useGameStore((s) => s.battle.playerHpCurrent);
  const battleActive = useGameStore((s) => s.battle.active);
  const [autoExit, setAutoExit] = useState(false);
  const armedRef = useRef(false);
  useEffect(() => {
    if (!battleActive) {
      armedRef.current = false;
      setAutoExit(false);
      return;
    }
    if (armedRef.current) return;
    if (opponentHp <= 0 || playerHp <= 0) {
      armedRef.current = true;
      setAutoExit(true);
    }
  }, [opponentHp, playerHp, battleActive]);

  const showExit = exiting ?? autoExit;

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background: SKY_GRADIENTS[biome],
        // One-shot entry: scene rises + settles. No layout shift after.
        animation: "battle-scene-rise 450ms steps(6, end) both",
        ...PIXELATED,
      }}
    >
      {/* Distant scenery: repeat-x silhouette strip across the horizon. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "44%",
          height: "20%",
          backgroundImage: asset(biome, "scenery"),
          backgroundRepeat: "repeat-x",
          backgroundPosition: "bottom left",
          backgroundSize: "auto 100%",
          ...PIXELATED,
        }}
      />

      {/* Near ground: repeat-x band across the bottom ~38%. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "38%",
          backgroundImage: asset(biome, "ground"),
          backgroundRepeat: "repeat-x",
          backgroundPosition: "bottom left",
          backgroundSize: "auto 100%",
          ...PIXELATED,
        }}
      />

      {/* Opponent platform: upper-right, under the front sprite.
          Mirrors the opponent zone anchor (safe-area top-right). */}
      <div
        style={{
          position: "absolute",
          top: "34%",
          right: "10%",
          width: "30%",
          height: "9%",
          backgroundImage: asset(biome, "platform"),
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center bottom",
          backgroundSize: "100% 100%",
          ...PIXELATED,
        }}
      />

      {/* Player platform: lower-left, under the back sprite.
          Mirrors the player zone anchor (safe-area bottom-left). */}
      <div
        style={{
          position: "absolute",
          bottom: "20%",
          left: "8%",
          width: "38%",
          height: "11%",
          backgroundImage: asset(biome, "platform"),
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center bottom",
          backgroundSize: "100% 100%",
          ...PIXELATED,
        }}
      />

      {/* ── Cinematic atmosphere (POSTFX) ──────────────────────────────────
          Layered OVER the pixel scene but UNDER the entry flash. All layers
          are pointerEvents:none and sit behind the fighters/cards/menu (which
          render as later siblings in BattleArena), so readability is intact. */}
      {POSTFX && (
        <>
          {/* Per-biome color grade — soft-light/multiply, low opacity. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: atmo.grade.color,
              mixBlendMode: atmo.grade.blend,
              opacity: atmo.grade.opacity,
            }}
          />

          {/* Soft radial light glow centered between the two fighter anchors,
              giving the pair a lit, atmospheric pop. Screen blend = additive. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              mixBlendMode: "screen",
              background: `radial-gradient(ellipse 55% 42% at 50% 56%, ${atmo.glow.color} 0%, transparent 70%)`,
              opacity: atmo.glow.opacity,
            }}
          />

          {/* Optional diagonal god-ray streak (grass/creative). */}
          {atmo.godray && (
            <div
              style={{
                position: "absolute",
                inset: "-20%",
                pointerEvents: "none",
                mixBlendMode: "screen",
                background: `linear-gradient(115deg, transparent 38%, ${atmo.godray} 50%, transparent 62%)`,
                animation: "battle-godray 11s ease-in-out infinite",
                willChange: "opacity, transform",
              }}
            />
          )}

          {/* Drifting ambient particles — pooled set, compositor-only motion
              (transform/opacity keyframes). Respects prefers-reduced-motion. */}
          <div
            className="battle-atmo-motes"
            style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}
          >
            {motes.map((m, i) => (
              <span
                key={i}
                style={{
                  position: "absolute",
                  left: `${m.left}%`,
                  top: `${m.top}%`,
                  width: m.size,
                  height: m.size,
                  borderRadius: "50%",
                  background: atmo.particles.color,
                  opacity: m.opacity,
                  boxShadow: `0 0 ${m.size * 1.5}px ${atmo.particles.color}`,
                  animation: `battle-mote-${atmo.particles.kind} ${m.dur}s linear ${m.delay}s infinite`,
                  // CSS var feeds the keyframe's horizontal sway amplitude.
                  ["--drift" as string]: `${m.drift}px`,
                  willChange: "transform, opacity",
                }}
              />
            ))}
          </div>

          {/* Stronger atmospheric vignette — darkens the panel edges for depth
              (ink, never pure black). Sits above grade/particles. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(ellipse 78% 80% at 50% 46%, transparent 52%, rgba(26,26,26,0.85) 100%)",
              opacity: atmo.vignette,
            }}
          />
        </>
      )}

      {/* Cream entry flash — one-shot, palette-safe (no pure white). */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "#f5ecd6",
          pointerEvents: "none",
          animation: "battle-scene-flash 450ms steps(4, end) both",
        }}
      />

      {/* One-shot EXIT: a dusk-tinted iris closes inward while the scene fades.
          Radial gradient mask + opacity, ~400ms, no layout shift. Only mounts
          once armed so it never blocks the live battle. */}
      {showExit && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            // Ink-dusk vignette (palette: ink #1a1a1a). Transparent center
            // shrinks to nothing as the iris closes.
            background:
              "radial-gradient(circle at 50% 52%, rgba(26,26,26,0) 0%, rgba(26,26,26,0) 38%, rgba(26,26,26,0.55) 60%, #1a1a1a 100%)",
            animation: "battle-scene-iris 400ms steps(6, end) both",
            zIndex: 5,
          }}
        />
      )}

      <style>{`
        @keyframes battle-scene-rise {
          0%   { transform: translateY(6%); }
          100% { transform: translateY(0); }
        }
        @keyframes battle-scene-flash {
          0%   { opacity: 0.85; }
          60%  { opacity: 0.85; }
          100% { opacity: 0; }
        }
        @keyframes battle-scene-iris {
          0%   { opacity: 0; transform: scale(2.2); }
          30%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1); }
        }
        /* Ambient motes — compositor-only (transform/opacity). "rise" floats
           upward with a gentle horizontal sway; "drift" wanders in place. */
        @keyframes battle-mote-rise {
          0%   { transform: translate3d(0, 0, 0); opacity: 0; }
          12%  { opacity: 1; }
          88%  { opacity: 1; }
          100% { transform: translate3d(var(--drift, 0px), -120%, 0); opacity: 0; }
        }
        @keyframes battle-mote-drift {
          0%   { transform: translate3d(0, 0, 0); opacity: 0; }
          20%  { opacity: 1; }
          50%  { transform: translate3d(var(--drift, 0px), -40%, 0); }
          80%  { opacity: 1; }
          100% { transform: translate3d(0, 0, 0); opacity: 0; }
        }
        @keyframes battle-godray {
          0%, 100% { opacity: 0.5; transform: translateX(-3%); }
          50%      { opacity: 1; transform: translateX(3%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .battle-atmo-motes span { animation: none !important; opacity: 0.35 !important; }
        }
      `}</style>
    </div>
  );
}
